import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Search, ChevronDown, ChevronRight, Building2, BedDouble,
  Wifi, Car, Zap, Dumbbell, CreditCard, RotateCcw, SlidersHorizontal,
  Waves, Tv, UtensilsCrossed, ShieldCheck, Snowflake, Flame,
  DoorOpen, Coffee, Users, Wine, Brush, Archive, Presentation,
  PawPrint, TreePine, Ban, PersonStanding, Beef, CookingPot,
  Accessibility, Camera, Luggage
} from 'lucide-react';

const AMENITY_ICONS = {
  'Wifi': Wifi, 'Air Conditioning': Snowflake, 'Parking': Car, 'Elevator': DoorOpen,
  'Power Backup': Zap, 'Swimming Pool': Waves, 'TV': Tv, 'Room Service': UtensilsCrossed,
  'Reception': Building2, 'Breakfast': Coffee, 'Room Heater': Flame, 'Smoking Room': Ban,
  'Fan': Dumbbell, 'Couple Friendly': Users, 'Alcohol Allowed': Wine, 'Housekeeping': Brush,
};

const FACILITY_ICONS = {
  'Security Camera': Camera, 'Luggage Storage': Luggage, 'Banquet': Archive,
  'Conference Room': Presentation, 'Pets Allowed': PawPrint, 'Outdoor Dining Area': TreePine,
  'Outside Food Allowed': UtensilsCrossed, 'No Smoking Inside': Ban,
  'Unmarried Couples Allowed': Users, 'Only Males Group Allowed': PersonStanding,
  'Non Veg Food Allowed': Beef, 'Restaurant': UtensilsCrossed, 'Kitchen': CookingPot,
  'WheelChair Access': Accessibility,
};

const FilterSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 last:border-0" data-testid={`filter-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 px-1 text-sm font-semibold text-gray-700 hover:text-teal-700 transition-colors"
        data-testid={`toggle-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="flex items-center gap-2">
          <Icon size={16} className="text-teal-600" />
          {title}
        </span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && <div className="pb-4 px-1 space-y-3">{children}</div>}
    </div>
  );
};

const MultiSelect = ({ label, options, selected, onChange, testId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const filtered = options.filter(o => o.toLowerCase().includes(searchTerm.toLowerCase()));

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  return (
    <div className="relative" data-testid={testId}>
      <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white hover:border-teal-300 transition-colors flex items-center justify-between"
      >
        <span className={selected.length > 0 ? 'text-gray-800' : 'text-gray-400'}>
          {selected.length > 0 ? `${selected.length} selected` : `Select ${label}`}
        </span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>
      {showDropdown && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-hidden">
          {options.length > 6 && (
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-teal-400 outline-none"
              />
            </div>
          )}
          <div className="max-h-40 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 p-2 text-center">No options</p>
            ) : filtered.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-teal-50 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="truncate">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {showDropdown && <div className="fixed inset-0 z-10" onClick={() => { setShowDropdown(false); setSearchTerm(''); }} />}
    </div>
  );
};

const ToggleFilter = ({ label, icon: Icon, value, onChange, testId }) => {
  const cycle = () => {
    if (!value) onChange('yes');
    else if (value === 'yes') onChange('no');
    else onChange(null);
  };
  return (
    <button
      onClick={cycle}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
        value === 'yes' ? 'bg-teal-50 border-teal-300 text-teal-700' :
        value === 'no' ? 'bg-red-50 border-red-300 text-red-600' :
        'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
      }`}
      title={`${label}: ${value === 'yes' ? 'Has' : value === 'no' ? 'Missing' : 'Any'}`}
      data-testid={testId}
    >
      {Icon && <Icon size={13} />}
      <span className="truncate max-w-[80px]">{label}</span>
      {value && (
        <span className={`ml-0.5 text-[10px] font-bold ${value === 'yes' ? 'text-teal-600' : 'text-red-500'}`}>
          {value === 'yes' ? 'Y' : 'N'}
        </span>
      )}
    </button>
  );
};

const initialFilters = {
  propertyName: '',
  ownerName: '',
  propertyTypes: [],
  status: '',
  cities: [],
  states: [],
  pinCode: '',
  createdDateFrom: '',
  createdDateTo: '',
  totalRoomsMin: '',
  totalRoomsMax: '',
  roomCategories: [],
  bedTypes: [],
  acStatuses: [],
  amenities: {},
  facilities: {},
  bankName: '',
  ifscCode: '',
  gstNumber: '',
  hasBankDetails: '',
  hasGST: '',
};

const PropertyAdvancedFilter = ({ isOpen, onClose, onApply, filterOptions = {} }) => {
  const [filters, setFilters] = useState(initialFilters);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateAmenity = useCallback((key, value) => {
    setFilters(prev => {
      const updated = { ...prev.amenities };
      if (value) updated[key] = value;
      else delete updated[key];
      return { ...prev, amenities: updated };
    });
  }, []);

  const updateFacility = useCallback((key, value) => {
    setFilters(prev => {
      const updated = { ...prev.facilities };
      if (value) updated[key] = value;
      else delete updated[key];
      return { ...prev, facilities: updated };
    });
  }, []);

  const activeCount = (() => {
    let c = 0;
    if (filters.propertyName) c++;
    if (filters.ownerName) c++;
    if (filters.propertyTypes.length) c++;
    if (filters.status) c++;
    if (filters.cities.length) c++;
    if (filters.states.length) c++;
    if (filters.pinCode) c++;
    if (filters.createdDateFrom || filters.createdDateTo) c++;
    if (filters.totalRoomsMin || filters.totalRoomsMax) c++;
    if (filters.roomCategories.length) c++;
    if (filters.bedTypes.length) c++;
    if (filters.acStatuses.length) c++;
    c += Object.keys(filters.amenities).length;
    c += Object.keys(filters.facilities).length;
    if (filters.bankName) c++;
    if (filters.ifscCode) c++;
    if (filters.gstNumber) c++;
    if (filters.hasBankDetails) c++;
    if (filters.hasGST) c++;
    return c;
  })();

  const handleApply = () => {
    const cleaned = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length > 0) cleaned[k] = v;
      else if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0) cleaned[k] = v;
      else if (typeof v === 'string' && v) cleaned[k] = v;
      else if (typeof v === 'number') cleaned[k] = v;
    });
    onApply(cleaned);
  };

  const handleReset = () => {
    setFilters(initialFilters);
    onApply({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="advanced-filter-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />

      {/* Sidebar */}
      <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-teal-600 to-teal-700">
          <div className="flex items-center gap-2.5">
            <SlidersHorizontal size={20} className="text-white" />
            <h3 className="text-base font-bold text-white">Advanced Filters</h3>
            {activeCount > 0 && (
              <span className="bg-white text-teal-700 text-xs font-bold px-2 py-0.5 rounded-full" data-testid="active-filter-count">
                {activeCount}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" data-testid="close-filter-panel">
            <X size={18} className="text-white" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {/* Section 1: Property Basics */}
          <FilterSection title="Property Basics" icon={Building2} defaultOpen={true}>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Property Name</label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={filters.propertyName}
                    onChange={e => updateFilter('propertyName', e.target.value)}
                    placeholder="Search property name..."
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                    data-testid="filter-property-name"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Owner Name</label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={filters.ownerName}
                    onChange={e => updateFilter('ownerName', e.target.value)}
                    placeholder="Search owner name..."
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                    data-testid="filter-owner-name"
                  />
                </div>
              </div>
              <MultiSelect
                label="Property Type"
                options={filterOptions.propertyTypes || []}
                selected={filters.propertyTypes}
                onChange={v => updateFilter('propertyTypes', v)}
                testId="filter-property-types"
              />
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
                <select
                  value={filters.status}
                  onChange={e => updateFilter('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid="filter-adv-status"
                >
                  <option value="">All</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <MultiSelect
                label="State"
                options={filterOptions.states || []}
                selected={filters.states}
                onChange={v => updateFilter('states', v)}
                testId="filter-adv-states"
              />
              <MultiSelect
                label="City"
                options={filterOptions.cities || []}
                selected={filters.cities}
                onChange={v => updateFilter('cities', v)}
                testId="filter-adv-cities"
              />
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Pin Code</label>
                <input
                  type="text"
                  value={filters.pinCode}
                  onChange={e => updateFilter('pinCode', e.target.value)}
                  placeholder="Search pin code..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid="filter-pincode"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Created Date Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={filters.createdDateFrom}
                    onChange={e => updateFilter('createdDateFrom', e.target.value)}
                    className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-teal-400 outline-none"
                    data-testid="filter-date-from"
                  />
                  <span className="text-xs text-gray-400">to</span>
                  <input
                    type="date"
                    value={filters.createdDateTo}
                    onChange={e => updateFilter('createdDateTo', e.target.value)}
                    className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-teal-400 outline-none"
                    data-testid="filter-date-to"
                  />
                </div>
              </div>
            </div>
          </FilterSection>

          {/* Section 2: Room Inventory */}
          <FilterSection title="Room Inventory" icon={BedDouble}>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Total Rooms Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={filters.totalRoomsMin}
                    onChange={e => updateFilter('totalRoomsMin', e.target.value)}
                    placeholder="Min"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                    data-testid="filter-adv-rooms-min"
                  />
                  <span className="text-xs text-gray-400">to</span>
                  <input
                    type="number"
                    value={filters.totalRoomsMax}
                    onChange={e => updateFilter('totalRoomsMax', e.target.value)}
                    placeholder="Max"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                    data-testid="filter-adv-rooms-max"
                  />
                </div>
              </div>
              <MultiSelect
                label="Room Categories"
                options={filterOptions.roomCategories || []}
                selected={filters.roomCategories}
                onChange={v => updateFilter('roomCategories', v)}
                testId="filter-room-categories"
              />
              <MultiSelect
                label="Bed Type"
                options={filterOptions.bedTypes || []}
                selected={filters.bedTypes}
                onChange={v => updateFilter('bedTypes', v)}
                testId="filter-bed-types"
              />
              <MultiSelect
                label="AC Status"
                options={filterOptions.acStatuses || []}
                selected={filters.acStatuses}
                onChange={v => updateFilter('acStatuses', v)}
                testId="filter-ac-statuses"
              />
            </div>
          </FilterSection>

          {/* Section 3: Amenities */}
          <FilterSection title="Amenities" icon={Wifi}>
            <div className="flex flex-wrap gap-1.5">
              {(filterOptions.amenityKeys || []).map(key => (
                <ToggleFilter
                  key={key}
                  label={key}
                  icon={AMENITY_ICONS[key]}
                  value={filters.amenities[key] || null}
                  onChange={v => updateAmenity(key, v)}
                  testId={`filter-amenity-${key.toLowerCase().replace(/\s+/g, '-')}`}
                />
              ))}
            </div>
          </FilterSection>

          {/* Section 4: Facilities */}
          <FilterSection title="Facilities" icon={ShieldCheck}>
            <div className="flex flex-wrap gap-1.5">
              {(filterOptions.facilityKeys || []).map(key => (
                <ToggleFilter
                  key={key}
                  label={key}
                  icon={FACILITY_ICONS[key]}
                  value={filters.facilities[key] || null}
                  onChange={v => updateFacility(key, v)}
                  testId={`filter-facility-${key.toLowerCase().replace(/\s+/g, '-')}`}
                />
              ))}
            </div>
          </FilterSection>

          {/* Section 5: Banking & GST */}
          <FilterSection title="Banking & GST" icon={CreditCard}>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Bank Name</label>
                <input
                  type="text"
                  value={filters.bankName}
                  onChange={e => updateFilter('bankName', e.target.value)}
                  placeholder="Search bank name..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid="filter-bank-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">IFSC Code</label>
                <input
                  type="text"
                  value={filters.ifscCode}
                  onChange={e => updateFilter('ifscCode', e.target.value)}
                  placeholder="Search IFSC..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid="filter-ifsc-code"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">GST Number</label>
                <input
                  type="text"
                  value={filters.gstNumber}
                  onChange={e => updateFilter('gstNumber', e.target.value)}
                  placeholder="Search GST number..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid="filter-gst-number"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Bank Details</label>
                  <select
                    value={filters.hasBankDetails}
                    onChange={e => updateFilter('hasBankDetails', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-teal-400 outline-none"
                    data-testid="filter-has-bank"
                  >
                    <option value="">Any</option>
                    <option value="yes">Has Bank Info</option>
                    <option value="no">No Bank Info</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">GST Available</label>
                  <select
                    value={filters.hasGST}
                    onChange={e => updateFilter('hasGST', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-teal-400 outline-none"
                    data-testid="filter-has-gst"
                  >
                    <option value="">Any</option>
                    <option value="yes">Has GST</option>
                    <option value="no">No GST</option>
                  </select>
                </div>
              </div>
            </div>
          </FilterSection>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center gap-3">
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="filter-clear-all"
          >
            <RotateCcw size={14} />
            Clear All
          </button>
          <button
            onClick={handleApply}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
            data-testid="filter-apply-btn"
          >
            <SlidersHorizontal size={14} />
            Apply Filters
            {activeCount > 0 && (
              <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">{activeCount}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyAdvancedFilter;
